/**
 * Sentry initialization — env-var driven.
 *
 * Reads `VITE_SENTRY_DSN` from the build-time environment. If unset (or
 * empty string), `initSentry()` no-ops cleanly — the SDK is not called,
 * no network requests happen, and the rest of the app behaves identically.
 * This lets the integration ship as inert code today and become live the
 * moment the env var is added in the Vercel dashboard, without a code
 * change.
 *
 * Optional secondary env vars:
 *   - `VITE_SENTRY_ENVIRONMENT` — defaults to import.meta.env.MODE
 *     (typically 'development' / 'production'). Override for finer
 *     buckets like 'staging' or 'preview'.
 *   - `VITE_SENTRY_RELEASE` — defaults to undefined (Sentry uses host).
 *     Wire this to a CI variable like the Vercel deploy SHA for proper
 *     release-tracking + source-map matching.
 *
 * Sampling:
 *   - tracesSampleRate 0.1 — 10% of page loads contribute performance
 *     traces. Zero in dev so test sessions don't pollute the dashboard.
 *   - Replay disabled by default (heavier bundle + privacy considerations).
 *     Add `Sentry.replayIntegration()` here later if needed.
 *
 * Manual capture:
 *   import * as Sentry from '@sentry/react'
 *   Sentry.captureException(err, { tags: { feature: 'x' } })
 */

import * as Sentry from '@sentry/react'

let initialized = false

export function initSentry() {
  if (initialized) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn || typeof dsn !== 'string' || !dsn.trim()) {
    // Silent no-op when no DSN is configured. The SDK never starts, so
    // calls to Sentry.captureException(...) elsewhere in the app become
    // safe no-ops too (the SDK is designed to be inert pre-init).
    return
  }

  Sentry.init({
    dsn:         dsn.trim(),
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'production',
    release:     import.meta.env.VITE_SENTRY_RELEASE     || undefined,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    // 10% of traces in prod; zero in dev so local tinkering doesn't
    // chew the org's quota.
    tracesSampleRate: import.meta.env.MODE === 'production' ? 0.1 : 0,
    // Don't send PII automatically; we already log uid/email via our
    // own errorReports pipeline only on render-boundary crashes.
    sendDefaultPii: false,
    // Filter known noise before it leaves the browser. Add patterns
    // here as we discover them.
    ignoreErrors: [
      // Known benign: network blips during route changes
      'NetworkError when attempting to fetch resource',
      // The chunk-load error is handled by lazyWithReload — surfacing
      // it to Sentry would just be alarm noise.
      /Loading chunk \S+ failed/i,
      /Failed to fetch dynamically imported module/i,
    ],
  })

  initialized = true
}

/**
 * Manual exception capture that's safe to call before initSentry runs
 * (or when no DSN is configured — both become no-ops).
 *
 * Prefer this over importing @sentry/react directly in feature code so
 * we have one chokepoint to add tags / breadcrumbs / sampling rules.
 */
export function captureException(err, context = {}) {
  if (!initialized) return
  try {
    Sentry.captureException(err, context)
  } catch {
    // Logging the logger is a tarpit. Swallow.
  }
}

/**
 * Send a non-exception message to Sentry — useful for high-stakes
 * recovery paths that aren't strictly errors but we want visibility
 * into. Examples:
 *   • silent tier fallback (VIN-2025-11)
 *   • ensureCellarForUser refused to auto-create
 *   • dispatcher skipped a send because prefs flipped mid-flight
 *
 * Set `level` to 'info' | 'warning' | 'error' to tier the alert
 * threshold. `context` accepts the same { tags, extra, level } shape
 * as captureException.
 *
 * Safe to call before initSentry (no-op) or in builds without the
 * DSN configured.
 */
export function captureMessage(message, context = {}) {
  if (!initialized) return
  try {
    Sentry.captureMessage(message, context)
  } catch { /* swallow */ }
}

/**
 * Tag the active Sentry session with the signed-in user so error
 * reports include uid + email instead of being anonymous. Critical
 * for triage — "this one user keeps hitting X" is unanswerable
 * without it.
 *
 * Email is included only when sendDefaultPii is false (it is — see
 * init config) because the user's email is already in our own
 * errorReports pipeline; consistency matters more than redundancy.
 *
 * Safe to call before initSentry (no-op) or with null (clears the
 * user, used on sign-out).
 */
export function setSentryUser(user) {
  if (!initialized) return
  try {
    if (user?.uid) {
      Sentry.setUser({
        id:    user.uid,
        email: user.email || undefined,
      })
    } else {
      Sentry.setUser(null)
    }
  } catch { /* tarpit */ }
}
